import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box, Paper, Typography, TextField, Button, Alert, InputAdornment,
    IconButton,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PhoneIcon from '@mui/icons-material/Phone';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HomeIcon from '@mui/icons-material/Home';
import useAuth from '../../hooks/useAuth';
import { authAPI } from '../../services/api';

export default function RegisterPage() {
    const [form, setForm] = useState({
        nom: '', prenom: '', email: '', password: '', password_confirmation: '',
        phone: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await authAPI.register(form);
            const user = await login(form.email, form.password);
            navigate('/trainer/candidatures');
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.errors;
            if (typeof msg === 'object') {
                setError(Object.values(msg).flat().join('. '));
            } else {
                setError(msg || 'Erreur lors de l\'inscription');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #1a5276 0%, #2e86c1 50%, #3498db 100%)',
            p: 2,
        }}>
            <Paper sx={{ maxWidth: 520, width: '100%', p: 4, borderRadius: 3 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h4" fontWeight={700} color="primary">
                        Créer un compte
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                        Inscrivez-vous en tant que formateur vacataire
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth label="Nom" value={form.nom}
                            onChange={handleChange('nom')} required
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><PersonIcon color="action" /></InputAdornment>,
                            }}
                        />
                        <TextField
                            fullWidth label="Prénom" value={form.prenom}
                            onChange={handleChange('prenom')} required
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><PersonIcon color="action" /></InputAdornment>,
                            }}
                        />
                    </Box>
                    <TextField
                        fullWidth label="Email" type="email" value={form.email}
                        onChange={handleChange('email')} required sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><EmailIcon color="action" /></InputAdornment>,
                        }}
                    />
                    <TextField
                        fullWidth label="Téléphone" value={form.phone}
                        onChange={handleChange('phone')} sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><PhoneIcon color="action" /></InputAdornment>,
                        }}
                    />
                    <TextField
                        fullWidth label="Mot de passe"
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        onChange={handleChange('password')} required sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><LockIcon color="action" /></InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        fullWidth label="Confirmer le mot de passe"
                        type={showPassword ? 'text' : 'password'}
                        value={form.password_confirmation}
                        onChange={handleChange('password_confirmation')} required sx={{ mb: 3 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><LockIcon color="action" /></InputAdornment>,
                        }}
                    />
                    <Button
                        type="submit" fullWidth variant="contained" size="large"
                        disabled={loading}
                        sx={{
                            background: 'linear-gradient(135deg, #1a5276, #2e86c1)',
                            textTransform: 'none', borderRadius: 2, py: 1.5, mb: 2,
                        }}
                    >
                        {loading ? 'Inscription...' : 'S\'inscrire'}
                    </Button>
                </form>

                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Déjà un compte ?{' '}
                        <Link to="/login" style={{ color: '#2e86c1', fontWeight: 600 }}>
                            Se connecter
                        </Link>
                    </Typography>
                    <Button
                        component={Link} to="/"
                        startIcon={<HomeIcon />}
                        sx={{ mt: 2, textTransform: 'none', color: 'text.secondary' }}
                    >
                        Retour aux offres
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
